import { getDateField, getMaxLengthError, getTextField } from '@lender/utils';
import dayjs from 'dayjs';
import { object } from 'yup';

import { MersTransferKind } from 'constants/loanActionEditor';
import { getCommonDateBody, getEasternToday } from 'utils/date';

import {
  BENEFICIAL_TRANSFER_DAYS_BACK_LIMIT,
  CONTROL_CHARACTER_REG_EXP,
  SALE_ID_MAX_LENGTH,
  SALE_ID_ONE_LINE_ERROR,
  TRANSFER_DATE_FIELDS,
  TRANSFER_DAYS_AHEAD_LIMIT,
  TransferDateError,
} from './MersTransferDetailsForm.constants';

// Calendar-day comparisons; an empty value or limit is left to the required / other checks.
const isOnOrAfter = (value, limit) => !value || !limit || !dayjs(value).isBefore(limit, 'day');
const isOnOrBefore = (value, limit) => !value || !limit || !dayjs(value).isAfter(limit, 'day');

// The transfer's date fields with their visibility and MERS's today-relative window (US-Eastern "today", as MERS
// counts it); minDate / maxDate are null where MERS sets no limit.
export const getTransferDateFields = ({ kind, flow }) => {
  const today = getEasternToday();

  return TRANSFER_DATE_FIELDS.map(field => ({
    ...field,
    label:
      kind === MersTransferKind.SERVICING_AND_BENEFICIAL_RIGHTS && field.comboLabel
        ? field.comboLabel
        : field.label,
    isHidden: !((flow && field.flowKinds) || field.kinds).includes(kind),
    minDate: field.daysBackLimitKinds.includes(kind)
      ? today.subtract(BENEFICIAL_TRANSFER_DAYS_BACK_LIMIT, 'day')
      : null,
    maxDate: field.isLimitedAhead ? today.add(TRANSFER_DAYS_AHEAD_LIMIT, 'day') : null,
  }));
};

const getTransferDateSchema = ({ isRequired, isHidden, minDate, maxDate }) =>
  getDateField({ isRequired: isRequired && !isHidden })
    .nullable()
    .test('within-days-back', TransferDateError.TOO_FAR_BACK, value => isOnOrAfter(value, minDate))
    .test('within-days-ahead', TransferDateError.TOO_FAR_AHEAD, value =>
      isOnOrBefore(value, maxDate),
    );

// Mirrors the backend's transfer checks (MersLoanOperationValidator.ValidateTransfer) so the operator hears about a
// date MERS would refuse before anything is sent. A hidden date is never entered, so its checks pass.
export const getMersTransferDetailsSchema = ({ kind, flow }) => {
  const { effectiveDate, beneficialEffectiveDate, saleDate } = Object.fromEntries(
    getTransferDateFields({ kind, flow }).map(field => [field.name, field]),
  );

  return object().shape({
    effectiveDate: getTransferDateSchema(effectiveDate).when('saleDate', (sale, schema) =>
      schema.test('on-or-after-sale-date', TransferDateError.BEFORE_SALE_DATE, value =>
        isOnOrAfter(value, sale),
      ),
    ),
    beneficialEffectiveDate: getTransferDateSchema(beneficialEffectiveDate).when(
      'effectiveDate',
      (servicingDate, schema) =>
        schema.test('on-or-before-servicing-date', TransferDateError.AFTER_SERVICING_DATE, value =>
          isOnOrBefore(value, servicingDate),
        ),
    ),
    saleDate: getTransferDateSchema(saleDate),
    saleId: getTextField({ isRequired: false })
      .max(SALE_ID_MAX_LENGTH, getMaxLengthError(SALE_ID_MAX_LENGTH))
      .test(
        'one-line',
        SALE_ID_ONE_LINE_ERROR,
        value => !CONTROL_CHARACTER_REG_EXP.test(value ?? ''),
      ),
  });
};

// Only the dates the transfer uses are sent; the rest go as null.
export const buildMersTransferDetails = (form, { kind, flow }) => ({
  ...Object.fromEntries(
    getTransferDateFields({ kind, flow }).map(({ name, isHidden }) => [
      name,
      isHidden ? null : getCommonDateBody(form[name]),
    ]),
  ),
  saleId: form.saleId?.trim() || null,
});
